<template>
  <div class="customers list">
    <div class="row mb-3">
      <div class="col-12">
        <div class="row">
          <h1>Upload Pv Bookings</h1>
        </div>
      </div>
      <div class="col-12">
        <div class="row">
          <div class="col-1"><b>File Upload:</b></div>
          <div
            class="file-upload col-2"
            :class="{ drag: isDragging }"
            @dragover.stop.prevent="onDragOver"
            @dragleave.stop.prevent="onDragLeave"
            @drop.stop.prevent="onDrop"
          >
            <label>
              <span
                class="btn btn-sm btn-outline-secondary photo-upload-button"
              >
                <div>
                  <!-- <i class="fas fa-plus"></i> -->
                  XLSX File
                </div>
              </span>
              <input
                type="file"
                accept=".xlsx"
                ref="file"
                @change="onFileChange"
              />
            </label>
            <div v-if="filename">{{ filename }}</div>
            <div v-if="fileData">
              <h6>Total {{ fileData.length }} Records</h6>
            </div>
          </div>
          <div v-if="filename && fileData && fileData.length" class="col-2">
              <button class="btn btn-sm btn-outline-secondary" @click.prevent="insertData()">
                <i class="fas fa-save"></i>
                Submit
              </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, inject, ref } from "vue";
import { useStore } from "vuex";
import * as XLSX from "xlsx";
import _ from "lodash";
import { useToast } from "vue-toastification";
import { Address, Booking, Customer, Tenant } from "@/models";
import moment from "moment";
const store = useStore();
const isDragging = ref(false);
const file = ref(null);
const actProperty: any = inject("actProperty");
const toasted = useToast();
const customerlist = computed(
  (): Customer[] => store.getters["pvbookings/customerlist"]
);
const addBookings = (bookings: Booking[]): Promise<Booking[]> => {
  return store.dispatch("pvbookings/addPvBookings", bookings);
};
// Define a ref to hold file data
const fileData = ref<Array<Record<string, any>> | null>(null);
const filename = ref("");

async function onFileChange(event: Event) {
  const target = event.target as HTMLInputElement;
  if (target.files && target.files.length > 0) {
    const file = target.files[0];
    filename.value = file.name;

    const reader = new FileReader();

    reader.onload = async (e) => {
      const data = new Uint8Array(e.target?.result as ArrayBuffer);
      const workbook = XLSX.read(data, { type: "array" });

      // Get the first sheet and convert it to an array of objects
      const worksheet = workbook.Sheets[workbook.SheetNames[0]];
      fileData.value = XLSX.utils.sheet_to_json(worksheet, { header: 1 });

      // Use header row as keys and convert each row into an object
      const headers = fileData.value[0];
      const rows = fileData.value.slice(1);
      // Map rows to objects and filter out blank objects
      fileData.value = rows
        .map((row) =>
          headers.reduce((acc, header, i) => {
            acc[header] = row[i];
            return acc;
          }, {} as Record<string, any>)
        )
        .filter((obj) =>
          Object.values(obj).some(
            (value) => value !== null && value !== undefined && value !== ""
          )
        );
      fileData.value = fileData.value.map((item) => ({
        ...item,
        "Due Date":
          typeof item["Due Date"] === "number"
            ? excelDateToJSDate(item["Due Date"])
            : item["Due Date"],
        Property: parseAddress(item["Property"], item["Postcode"]),
      }));
    };
    reader.readAsArrayBuffer(file);
  }
}

const insertData = async () => {
  if (customerlist.value.length === 0) {
    await getCustomersAction();
  }
  let apiJson = convertToBookingModel(fileData.value);
  try {
    await addBookings(apiJson);
    toasted.success("Successfully Inserted Buck Records");
    fileData.value = null;
    filename.value = '';
    setTimeout(() => {
      window.location.reload();
    }, 5000);
  } catch (err: any) {
    actProperty.displayError(err);
  }
};
const getCustomersAction = async () => {
  try {
    await store.dispatch("pvbookings/getCustomers");
  } catch (err: any) {
    actProperty.displayError(err);
  }
};

// Function to convert tenant data into an array of Tenant instances
const convertToTenantArray = (data: any): Tenant[] => {
  const tenants: Tenant[] = [];
  let i = 1;

  while (data[`Tenant ${i}`]) {
    tenants.push(
      new Tenant({
        ttname: data[`Tenant ${i}`],
        ttemail: data[`Tenant ${i} Email`],
        ttmobile: data[`Tenant ${i} Mobile`],
      })
    );
    i++;
  }

  return tenants;
};

const subjobendDate = (time: string, date: Date) => {
  let value = "";
  if (time) {
    let dt: Date = date;
    let justdate = moment(dt).utc().format("YYYY-MM-DD");
    let justtime = moment(time, "hh:mm A").format("HH:mm");
    value = `${justdate}T${justtime}:00.000Z`;
  }
  return value;
};

// Assuming you have a function parseAddress as defined earlier
function convertToBookingModel(Datas: any[]): Booking[] {
  return Datas.map((data) => {
    const startTime = "T09:00:00.000Z";
    const endTime = "T10:00:00.000Z";
    let startdate = subjobendDate(startTime, data["Due Date"]);
    let enddate = subjobendDate(endTime, data["Due Date"]);
    let tenants = convertToTenantArray(data);
    let customer = customerlist.value.find(
      (f: Customer) =>
        f.companyName == data["Template"] && f.branchName == data["Branch"]
    );
    // Create a new BookingModel instance using the data
    const booking = new Booking({
      jobtype: data["Job Type"].toLowerCase(),
      address: data["Property"],
      duedate: data["Due Date"],
      startdate: startdate,
      enddate: enddate,
      tenancyid: data["ID"].toString(),
      tenants: tenants,
      pmname: data["Property Manager"],
      pmemail: data["Property Manager Email"],
      customer: customer,
    });
    delete booking.qc;
    return booking;
  });
}
// Function to parse address string into Address
function parseAddress(address: string, postcode: string): Address {
  // Flexible regex to handle addresses with building names, street, town, and postcode
  const parts = address.split(",").map((part) => part.trim());
  if (!parts.length) {
    throw new Error("Address format not recognized");
  }
  // Return new Address instance using the parsed data
  return new Address({
    line1: parts[1].trim(), // Street address
    line2: parts[2]?.trim(), // Optional second address line
    town: parts[3]?.trim(), // Town or city
    postcode: postcode, // Postcode (passed directly)
    country: "London", // Default to "London" as country
  });
}
// Convert serial number to JavaScript date
function excelDateToJSDate(serial) {
  const utc_days = Math.floor(serial - 25569);
  const utc_value = utc_days * 86400;
  const date_info = new Date(utc_value * 1000);

  return new Date(
    date_info.getFullYear(),
    date_info.getMonth(),
    date_info.getDate()
  );
}

// Methods converted to the Composition API style
const onDragOver = (event: DragEvent) => {
  isDragging.value = true;
};

const onDragLeave = (event: MouseEvent) => {
  if (event.currentTarget === event.target) {
    isDragging.value = false;
  }
};

const onDrop = (event: DragEvent) => {
  isDragging.value = false;
  onFileChange(event);
};
</script>

<!-- Add "scoped" attribute to limit CSS to this component only -->
<style scoped lang="scss">
@import "@/assets/sass/bootstrap/_variables.scss";

.drag * {
  pointer-events: none;
}

.file-upload {
  border: 2px dashed rgba(0, 0, 0, 0);
  border-radius: 0.2rem;
  box-sizing: border-box;
  width: 100%;
  &.drag {
    background-color: $info-semi-opaque;
    border-color: $info;
  }
}

label {
  margin-bottom: 0;
  input[type="file"] {
    display: none;
  }
}
</style>
